import React from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import styled from "styled-components";
import { colors, radii } from "@libertify/ui/theme-v2";
import { getCoverImage } from "../../workspaces/helpers/workspaceImages";

const T_PREFIX = "media.projects.analytics.header";

export type AnalyticsHeaderProps = {
  projectId: string | undefined;
  title: string;
  /** The project image: undefined while loading, null when it has none. */
  image: string | null | undefined;
};

/** Cover banner of the project analytics pages: project image and title. */
export const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({
  projectId,
  title,
  image,
}) => {
  const t = useTranslate();
  return (
    <StyledBanner $background={bannerBackground(image, projectId)}>
      <Flex
        vertical
        justify="flex-end"
        style={{ position: "relative", minHeight: 168, padding: "20px 24px" }}
      >
        <StyledTitle level={2} ellipsis={{ tooltip: title }}>
          {title}
        </StyledTitle>
        <StyledLabel>{t(`${T_PREFIX}.label`)}</StyledLabel>
      </Flex>
    </StyledBanner>
  );
};

// The workspace-style gradient sits under the project image, so it shows
// while loading, without an image, or if the image fails to load.
const bannerBackground = (
  image: string | null | undefined,
  projectId: string | undefined
) => {
  const cover = getCoverImage(projectId ?? "") ?? colors.border.subtle;
  return image
    ? `url("${image.replace(/"/g, "%22")}") center / cover no-repeat, ${cover}`
    : cover;
};

// ─── Styled components ───

const StyledBanner = styled.div<{ $background: string }>`
  position: relative;
  overflow: hidden;
  border-radius: ${radii.box}px;
  background: ${({ $background }) => $background};

  /* Darkens the bottom of the photo so the white title stays readable. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgb(15 23 42 / 0%) 0%,
      rgb(15 23 42 / 62%) 100%
    );
  }
`;

const StyledTitle = styled(Typography.Title)`
  && {
    margin: 0;
    color: ${colors.text.onAccent};
    font-weight: 600;
    text-shadow: 0 1px 6px rgb(0 0 0 / 35%);
  }
`;

const StyledLabel = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgb(255 255 255 / 85%);
  text-shadow: 0 1px 4px rgb(0 0 0 / 30%);
`;
